<template>
  <!-- The reverse index for the opened word: Greek → Hebrew, Hebrew → Greek, or a Latin word's columns; per translator. -->
  <aside v-if="opened" class="index-panel" aria-label="Reverse index">
    <header class="panel-head">
      <div class="panel-actions">
        <button v-if="canGoBack" class="plain" @click="back" aria-label="Back">←</button>
        <span class="direction">{{ direction }}</span>
        <button class="plain close" @click="closeIndex" aria-label="Close">×</button>
      </div>
      <div class="word">
        <span class="headword" :class="font(opened.lexeme)">{{ opened.lexeme.headword }}</span>
        <span v-if="opened.lexeme.translit" class="translit">{{ opened.lexeme.translit }}</span>
        <span v-if="opened.surface !== opened.lexeme.headword" class="surface" :class="font(opened.lexeme)"
          >({{ opened.surface }})</span
        >
      </div>
      <p v-if="opened.lexeme.gloss" class="gloss">{{ opened.lexeme.gloss }}</p>
      <p v-if="opened.counterpart.length && info.relation" class="here">
        Here it {{ info.relation }}
        <a v-for="c in opened.counterpart" :key="c.key" :class="font(c)" @click="pivot(c)">{{ c.headword }}</a>
        <span v-if="!opened.confident" class="unconfirmed">(unconfirmed)</span>
      </p>
    </header>

    <nav class="tabs-bar">
      <button v-for="t in tabs" :key="t.id" class="tab" :class="{ 'is-active': tab === t.id }" @click="tab = t.id">
        {{ t.label }}
      </button>
    </nav>

    <div v-if="tab === 'lexicons'" class="panel-body">
      <p v-if="layerLoading" class="muted">Loading…</p>
      <p v-else-if="layerError" class="muted">{{ layerError }}</p>
      <template v-else-if="layer">
        <p v-if="!layer.cards.length" class="muted">No lexicon has an entry for this word.</p>
        <LexiconCard v-for="(c, i) in layer.cards" :key="i" :card="c" @open-strongs="openStrongs" />
      </template>
    </div>

    <div v-else-if="tab === 'translators'" class="panel-body">
      <p v-if="layerLoading" class="muted">Loading…</p>
      <p v-else-if="layerError" class="muted">{{ layerError }}</p>
      <template v-else-if="layer">
        <h4 class="section">How each column renders it</h4>
        <table class="consistency">
          <tbody>
            <tr v-for="c in layer.consistency" :key="c.column">
              <td class="font-greek column-name">{{ c.column }}</td>
              <td v-if="c.lexemes[0]">
                <a class="font-greek" @click="pivot(c.lexemes[0].dominant)">{{ c.lexemes[0].dominant.headword }}</a>
                <span class="muted share">{{ percent(c.lexemes[0].dominant_share) }} of {{ c.lexemes[0].total }}</span>
              </td>
              <td v-if="c.lexemes[0]" class="muted n" title="distinct Greek words">
                {{ c.lexemes[0].distinct }} {{ c.lexemes[0].distinct === 1 ? 'word' : 'words' }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!layer.consistency.length" class="muted">Not a content word, or not rendered.</p>
        <template v-if="layer.agreement && layer.agreement.pairs.length">
          <h4 class="section">How the columns agree at it</h4>
          <AgreementTable :pairs="layer.agreement.pairs" :scope="{ hebrew: opened.lexeme.key }" />
        </template>
      </template>
    </div>

    <div v-else class="panel-body">
      <p v-if="loading" class="muted">Loading…</p>
      <p v-else-if="error" class="muted">{{ error }}</p>
      <template v-else-if="data">
        <p class="muted total">{{ data.total }} occurrences</p>
        <section v-for="c in data.columns" :key="c.column" class="column">
          <h3>
            <span class="column-name font-greek">{{ c.column }}</span>
            <span class="muted">{{ c.total }}</span>
          </h3>
          <ul>
            <li v-for="r in shown(c)" :key="r.lexeme.key">
              <div class="rendering">
                <a class="lexeme" :class="font(r.lexeme)" @click="pivot(r.lexeme)">{{ r.lexeme.headword }}</a>
                <span v-if="r.lexeme.gloss" class="gloss">{{ r.lexeme.gloss }}</span>
                <button class="count" @click="toggleRefs(c.column, r.lexeme.key)">
                  {{ r.count
                  }}<span v-if="r.unconfirmed" class="unconfirmed-count" title="unconfirmed"
                    >·{{ r.unconfirmed }}?</span
                  >
                </button>
              </div>
              <RefList
                v-if="refsOpen === refKey(c.column, r.lexeme.key)"
                :language="language"
                :lexeme="opened.lexeme.key"
                :column="c.column"
                :counterpart="r.lexeme.key"
                origin="head"
              />
            </li>
            <li v-if="c.unaligned" class="unaligned">
              <span class="muted">{{ info.unaligned }}</span>
              <button class="count" @click="toggleRefs(c.column, null)">{{ c.unaligned }}</button>
              <RefList
                v-if="refsOpen === refKey(c.column, null)"
                :language="language"
                :lexeme="opened.lexeme.key"
                :column="c.column"
                :counterpart="null"
                origin="head"
              />
            </li>
            <li v-for="b in beyond(c)" :key="b.origin" class="beyond">
              <span class="muted">{{ b.label }}</span>
              <button class="count" @click="toggleRefs(c.column, null, b.origin)">{{ b.count }}</button>
              <RefList
                v-if="refsOpen === refKey(c.column, null, b.origin)"
                :language="language"
                :lexeme="opened.lexeme.key"
                :column="c.column"
                :counterpart="null"
                :origin="b.origin"
              />
            </li>
          </ul>
          <button
            v-if="c.renderings.length > limit && !expanded.includes(c.column)"
            class="plain more"
            @click="expanded.push(c.column)"
          >
            {{ c.renderings.length - limit }} more
          </button>
        </section>
      </template>
    </div>
  </aside>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import { IndexLexeme, isRendered, Language, LANGUAGES } from '@/components/lemmas/renderer/wordLayer'
import { BEYOND_KEYS, ColumnSummary, IndexSummary, lexemeLayer, LexemeLayer, Origin, ORIGINS, summary } from '../api'
import { font, percent } from '../format'
import { back, closeIndex, indexState, openLexeme } from '../indexState'
import AgreementTable from './AgreementTable.vue'
import LexiconCard from './LexiconCard.vue'
import RefList from './RefList.vue'

export default defineComponent({
  name: 'IndexPanel',
  components: { AgreementTable, LexiconCard, RefList },
  setup() {
    const opened = computed(() => indexState.opened)
    const canGoBack = computed(() => indexState.trail.length > 0)
    const language = computed<Language>(() => opened.value?.lexeme.language ?? 'greek')
    const info = computed(() => LANGUAGES[language.value])
    // The Hebrew is what the translators render: its translators tab.
    const isHebrew = computed(() => isRendered(language.value))
    const direction = computed(() =>
      info.value.counterpart ? `${info.value.name} → ${LANGUAGES[info.value.counterpart].name}` : info.value.name
    )
    const data = ref<IndexSummary | null>(null)
    const loading = ref(false)
    const error = ref('')
    const refsOpen = ref<string | null>(null)
    const expanded = ref<string[]>([])
    const limit = 6
    type Tab = 'renderings' | 'lexicons' | 'translators'
    const tab = ref<Tab>('renderings')
    const tabs = computed(() => [
      { id: 'renderings' as Tab, label: 'Renderings' },
      { id: 'lexicons' as Tab, label: 'Lexicons' },
      ...(isHebrew.value ? [{ id: 'translators' as Tab, label: 'Translators' }] : []),
    ])
    const layer = ref<LexemeLayer | null>(null)
    const layerLoading = ref(false)
    const layerError = ref('')
    // Cards and translators load when their tab is first shown.
    watch(
      [opened, tab],
      async ([o, t]) => {
        if (!o || t === 'renderings') return
        if (t === 'translators' && !isRendered(o.lexeme.language)) tab.value = 'renderings'
        layerLoading.value = true
        layerError.value = ''
        try {
          layer.value = await lexemeLayer(o.lexeme.language, o.lexeme.key)
        } catch (e) {
          layerError.value = e instanceof Error ? e.message : 'Unavailable.'
        } finally {
          layerLoading.value = false
        }
      },
      { immediate: true },
    )

    watch(
      opened,
      async (o) => {
        data.value = null
        layer.value = null
        error.value = ''
        refsOpen.value = null
        expanded.value = []
        if (!o) return
        loading.value = true
        try {
          data.value = await summary(o.lexeme.language, o.lexeme.key)
        } catch (e) {
          error.value = 'The index is unavailable right now.'
        } finally {
          loading.value = false
        }
      },
      { immediate: true },
    )

    const shown = (c: ColumnSummary) =>
      expanded.value.includes(c.column) ? c.renderings : c.renderings.slice(0, limit)
    const refKey = (column: string, other: string | null, origin: Origin = 'head') =>
      `${column}|${other ?? ''}|${origin}`
    const toggleRefs = (column: string, other: string | null, origin: Origin = 'head') => {
      const k = refKey(column, other, origin)
      refsOpen.value = refsOpen.value === k ? null : k
    }
    // Beside a column's printed readings: its words beyond them, by origin.
    const beyond = (c: ColumnSummary) =>
      BEYOND_KEYS.map((origin) => ({ origin, count: c.beyond?.[origin] ?? 0, label: ORIGINS[origin].counted }))
        .filter((b) => b.count > 0)
    // Following a rendering opens its own index, in the other direction.
    const pivot = (lexeme: IndexLexeme) =>
      openLexeme({
        surface: lexeme.headword,
        lexeme,
        counterpart: opened.value ? [opened.value.lexeme] : [],
        confident: true,
      })

    // A Strong's cross-reference in a card opens that lexeme.
    const openStrongs = (key: string) =>
      openLexeme({
        surface: key,
        lexeme: { key, language: 'hebrew', headword: key, translit: '', gloss: '' },
        counterpart: [],
        confident: true,
      })

    return {
      opened,
      canGoBack,
      isHebrew,
      language,
      info,
      direction,
      data,
      loading,
      error,
      refsOpen,
      expanded,
      limit,
      tab,
      tabs,
      layer,
      layerLoading,
      layerError,
      font,
      shown,
      refKey,
      toggleRefs,
      beyond,
      pivot,
      openStrongs,
      percent,
      back,
      closeIndex,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/_colors';

.index-panel {
  position: fixed;
  top: 4em;
  right: 0;
  bottom: 0;
  width: min(30rem, 100vw);
  z-index: 35;
  background: $surface;
  border-left: 1px solid $border-strong;
  box-shadow: -2px 0 6px rgba($chrome, 0.08);
  display: flex;
  flex-direction: column;
}

.panel-head {
  padding: 0.75rem 1rem;
  background: $surface-raised;
  border-bottom: 1px solid $border;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .direction {
    flex: 1;
    color: $text-light;
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
}

.word {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;

  .headword {
    font-size: 1.8rem;
    color: $text-dark;
  }
}

.translit,
.surface,
.gloss,
.muted {
  color: $text-light;
}

.gloss {
  font-size: 0.85rem;
}

.here {
  margin-top: 0.4rem;
  font-size: 0.9rem;

  a {
    margin-left: 0.3rem;
    font-size: 1.1rem;
  }
}

.unconfirmed {
  color: $text-light;
  font-style: italic;
  font-size: 0.8rem;
}

.tabs-bar {
  display: flex;
  gap: 0.25rem;
  padding: 0 1rem;
  border-bottom: 1px solid $border;
  background: $surface-raised;

  .tab {
    border: 0;
    background: none;
    padding: 0.4rem 0.6rem;
    border-bottom: 2px solid transparent;
    color: $text-medium;
    cursor: pointer;
    font-size: 0.85rem;

    &.is-active {
      color: $text-dark;
      border-bottom-color: $interactive;
    }
  }
}

.section {
  color: $text-light;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0.75rem 0 0.3rem;
}

.consistency {
  width: 100%;
  font-size: 0.9rem;

  td {
    padding: 0.15rem 0.3rem;
    border-bottom: 1px solid $border;
  }

  .column-name {
    width: 4rem;
    color: $text-dark;
  }

  .share {
    margin-left: 0.4rem;
  }

  .n {
    text-align: right;
    font-size: 0.8rem;
  }
}

.panel-body {
  overflow-y: auto;
  padding: 0.75rem 1rem 2rem;
}

.total {
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
}

.column {
  margin-bottom: 1rem;

  h3 {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    border-bottom: 1px solid $border;
    margin-bottom: 0.3rem;

    .column-name {
      font-size: 1.2rem;
      color: $text-dark;
    }
  }

  ul {
    list-style: none;
    margin: 0;
  }
}

.rendering,
.unaligned,
.beyond {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.15rem 0;

  .lexeme {
    font-size: 1.1rem;
  }

  .gloss {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
  }
}

.unaligned,
.beyond {
  justify-content: space-between;
  flex-wrap: wrap;
  font-size: 0.8rem;

  // Its verses on their own line beneath, as a rendering's are.
  > .ref-list {
    flex-basis: 100%;
  }
}

.count {
  margin-left: auto;
  min-width: 2.5rem;
  padding: 0 0.4rem;
  border: 1px solid $border;
  border-radius: 3px;
  background: $surface;
  color: $interactive;
  cursor: pointer;
  font-variant-numeric: tabular-nums;

  &:hover {
    background: $interactive-soft;
  }
}

.unconfirmed-count {
  color: $text-light;
  font-size: 0.75em;
}

.plain {
  border: 0;
  background: none;
  cursor: pointer;
  color: $interactive;
  font-size: 1.1rem;
  padding: 0 0.25rem;
}

.more {
  font-size: 0.8rem;
}

a {
  cursor: pointer;
}
</style>
